import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { log } from '../../lib/logger';
import { Field } from './ProductsTab';

const EMPTY_FORM = { email: '', name: '', password: '', role: 'staff' };

// External effect body — keeps the hook body tiny.
const fetchStaff = async (setUsers) => {
  try { const { data } = await api.get('/auth/staff'); setUsers(data); }
  catch (err) { log.warn('Staff list fetch failed (admin only)', err); }
};

const StaffNewForm = ({ form, setForm, busy, onSubmit, onCancel }) => (
  <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-xl p-5 mb-5 space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" /></Field>
      <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></Field>
      <Field label="Password (min 6 chars)"><input required minLength={6} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input" /></Field>
      <Field label="Role">
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input">
          <option value="staff">Staff (edit content/products)</option>
          <option value="admin">Admin (full access)</option>
        </select>
      </Field>
    </div>
    <div className="flex justify-end gap-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
      <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg disabled:opacity-60">
        {busy && <Loader2 size={14} className="animate-spin" />} Add staff
      </button>
    </div>
  </form>
);

const StaffRow = ({ user, isSelf, onRemove }) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-100 last:border-0">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-sm font-semibold uppercase">
        {(user.name || user.email).charAt(0)}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm text-[#0a0a0a] truncate">{user.name || user.email}</div>
        <div className="text-xs text-slate-500 truncate">{user.email}</div>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${user.role === 'admin' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{user.role}</span>
      {!isSelf && (
        <button onClick={onRemove} className="p-2 text-slate-500 hover:text-red-600 rounded-lg hover:bg-slate-100" title="Remove"><Trash2 size={15} /></button>
      )}
    </div>
  </div>
);

const StaffTab = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => fetchStaff(setUsers), [setUsers, fetchStaff]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/staff', form);
      toast.success('Staff added');
      setShowNew(false); setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/auth/staff/${u.id}`); toast.success('Removed'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-[#0a0a0a]">Staff accounts ({users.length})</h2>
          <p className="text-sm text-slate-500">Only admins can add or remove staff.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg">
          <Plus size={16} /> Add staff
        </button>
      </div>
      {showNew && <StaffNewForm form={form} setForm={setForm} busy={busy} onSubmit={submit} onCancel={() => setShowNew(false)} />}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {users.map((u) => (
          <StaffRow key={u.id} user={u} isSelf={u.id === me?.id} onRemove={() => remove(u)} />
        ))}
        {users.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">Only admins can list staff. Sign in as an admin.</div>
        )}
      </div>
    </div>
  );
};

export default StaffTab;
